import { QueryClientProvider, useMutationState } from "@tanstack/react-query";
import {
  Breadcrumb,
  Toaster,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  EstadoDeErro,
  EstadoVazio,
  Separator,
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@base/ui";
import { Building2 } from "lucide-react";
import { useEffect, useState } from "react";
import {
  ProvedorDeClienteSelecionado,
  ProvedorDeClientes,
  useClienteSelecionado,
} from "./modules/clientes/application/clientes.contexto";
import { ClientesGatewayHttp } from "./modules/clientes/infrastructure/clientes.gateway";
import { ProvedorDaCentral } from "./modules/central/application/central.contexto";
import { CentralGatewayHttp } from "./modules/central/infrastructure/central.gateway.http";
import { ProvedorDeDashboard } from "./modules/dashboard/application/dashboard.contexto";
import { DashboardGatewayHttp } from "./modules/dashboard/infrastructure/dashboard.gateway.http";
import { ProvedorDeConexoes } from "./modules/conexoes/application/conexoes.contexto";
import { ProvedorDeFunilProjetado } from "./modules/funil-projetado/application/funil-projetado.contexto";
import { FunilProjetadoGatewayHttp } from "./modules/funil-projetado/infrastructure/funil-projetado.gateway";
import { ConexoesGatewayHttp } from "./modules/conexoes/infrastructure/conexoes.gateway";
import {
  deveExibirVerificacaoDeSessao,
  esquecerSessaoConhecida,
  haviaSessaoConhecida,
  lembrarSessaoConhecida,
} from "./modules/usuarios/application/sessao-conhecida";
import {
  TelaDeVerificacaoDeSessao,
  useMinimoDaTelaDeSessao,
} from "./modules/usuarios/presentation/tela-de-verificacao-de-sessao";
import { AvisoDeSincronizacao } from "./modules/conexoes/presentation/aviso-de-sincronizacao";
import { ROTULO_DO_PAPEL_NO_CLIENTE } from "./modules/clientes/domain/papeis-no-cliente";
import { SeletorDeCliente } from "./modules/clientes/presentation/seletor-de-cliente";
import { useAlertaDeSaldoNoLogin } from "./modules/conexoes/presentation/alerta-de-saldo";
import { useLoginRecente } from "./modules/usuarios/application/login-recente";
import { useSair, useSessao, chavesDeUsuarios } from "./modules/usuarios/application/use-usuarios";
import { ProvedorDeUsuarios } from "./modules/usuarios/application/usuarios.contexto";
import { ROTULO_DO_PAPEL, iniciaisDe, podeGerenciarUsuarios } from "./modules/usuarios/domain/papeis";
import { UsuariosGatewayHttp } from "./modules/usuarios/infrastructure/usuarios.gateway";
import { PaginaDeLogin } from "./modules/usuarios/presentation/pagina-de-login";
import { PaginaDefinirSenha } from "./modules/usuarios/presentation/pagina-definir-senha";
import { PaginaEsqueciSenha } from "./modules/usuarios/presentation/pagina-esqueci-senha";
import { PaginaDePrimeiroAcesso } from "./modules/usuarios/presentation/pagina-de-primeiro-acesso";
import { ClienteHttp } from "./shared/http/cliente-http";
import { mensagemDoErro } from "./shared/http/mensagem-de-erro";
import { AppSidebar } from "./shared/layout/app-sidebar";
import {
  AREAS_DO_CLIENTE,
  estaNaCentral,
  gruposDoMenu,
  paginaPorCaminho,
  podeVer,
  tituloDaArea,
  type ContextoDoMenu,
} from "./menu";
import { navegar, parametroDaUrl, usarCaminho } from "./shared/navegacao/navegacao";
import { criarClienteDeConsulta } from "./shared/query/cliente-de-consulta";

/**
 * Composicao da aplicacao. Equivale ao `AppModule` da API: e o unico lugar que
 * escolhe implementacao concreta e decide qual tela aparece.
 *
 * A autenticacao e por cookie de sessao httpOnly: o front nunca ve nem guarda o
 * token. Quando a API responde 401 (sessao expirada ou revogada), o cliente
 * HTTP zera a sessao em cache e a pessoa volta para o login.
 */
const clienteDeConsulta = criarClienteDeConsulta();

export const http = new ClienteHttp(
  `${import.meta.env.VITE_URL_DA_API ?? ""}/api`,
  () => null,
  globalThis.fetch.bind(globalThis),
  () => clienteDeConsulta.setQueryData(chavesDeUsuarios.sessao, null),
);

const gateway = new UsuariosGatewayHttp(http);
const gatewayDeClientes = new ClientesGatewayHttp(http);
// Numeros reais do Kommo; sem Kommo mapeado, o dashboard vem zerado.
const gatewayDoDashboard = new DashboardGatewayHttp(http);
const gatewayDeConexoes = new ConexoesGatewayHttp(http);
const gatewayDoFunilProjetado = new FunilProjetadoGatewayHttp(http);
const gatewayDaCentral = new CentralGatewayHttp(http);

const PAGINAS_DE_SENHA = { "/definir-senha": "convite", "/redefinir-senha": "redefinicao" } as const;
const PAGINAS_SEM_LOGIN = new Set(["/entrar", "/esqueci-senha"]);

export function App() {
  return (
    <QueryClientProvider client={clienteDeConsulta}>
      <ProvedorDeUsuarios gateway={gateway}>
        <ProvedorDeClientes gateway={gatewayDeClientes}>
          <ProvedorDeConexoes gateway={gatewayDeConexoes}>
            <ProvedorDeFunilProjetado gateway={gatewayDoFunilProjetado}>
              <ProvedorDeDashboard gateway={gatewayDoDashboard}>
                <ProvedorDaCentral gateway={gatewayDaCentral}>
                  <Rotas />
                  <Toaster />
                </ProvedorDaCentral>
              </ProvedorDeDashboard>
            </ProvedorDeFunilProjetado>
          </ProvedorDeConexoes>
        </ProvedorDeClientes>
      </ProvedorDeUsuarios>
    </QueryClientProvider>
  );
}

function Rotas() {
  const caminho = usarCaminho();
  const sessao = useSessao();
  const [conheciaSessao] = useState(haviaSessaoConhecida);
  const statusDoLogin = useMutationState({
    filters: { mutationKey: chavesDeUsuarios.entrar },
    select: (mutacao) => mutacao.state.status,
  }).at(-1);
  const splashDoLogin = statusDoLogin === "pending" || statusDoLogin === "success";
  const minimoDaTela = useMinimoDaTelaDeSessao(undefined, conheciaSessao || splashDoLogin);
  const [aviso, setAviso] = useState<string | null>(null);
  const logado = Boolean(sessao.data);
  const conferindoSessao = deveExibirVerificacaoDeSessao({
    conheciaSessao,
    pendente: sessao.isPending,
    minimoPassou: minimoDaTela,
    ...(statusDoLogin ? { statusDoLogin } : {}),
  });

  useEffect(() => {
    if (sessao.data) lembrarSessaoConhecida();
    else if (sessao.isFetched && !sessao.data) esquecerSessaoConhecida();
  }, [sessao.data, sessao.isFetched]);

  // Destino quando a pessoa esta na tela errada para o estado da sessao.
  const destino = conferindoSessao
    ? null
    : caminho in PAGINAS_DE_SENHA
      ? null
      : !logado && !PAGINAS_SEM_LOGIN.has(caminho)
        ? "/entrar"
        : logado && PAGINAS_SEM_LOGIN.has(caminho)
          ? "/"
          : null;

  useEffect(() => {
    if (destino && destino !== caminho) navegar(destino, { substituir: true });
  }, [destino, caminho]);

  if (caminho in PAGINAS_DE_SENHA) {
    return (
      <PaginaDeSenha
        finalidade={PAGINAS_DE_SENHA[caminho as keyof typeof PAGINAS_DE_SENHA]}
        aoConcluir={(mensagem) => {
          // A troca de senha encerra todas as sessoes no servidor.
          clienteDeConsulta.setQueryData(chavesDeUsuarios.sessao, null);
          setAviso(mensagem);
          navegar("/entrar", { substituir: true });
        }}
      />
    );
  }

  if (conferindoSessao) {
    return <TelaDeVerificacaoDeSessao />;
  }

  if (sessao.isError) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-page-background p-6">
        <EstadoDeErro
          className="w-full max-w-md"
          mensagem={mensagemDoErro(sessao.error)}
          aoTentarNovamente={() => void sessao.refetch()}
        />
      </div>
    );
  }

  if (!sessao.data) {
    return caminho === "/esqueci-senha" ? <PaginaEsqueciSenha /> : <PaginaDeLogin aviso={aviso} />;
  }

  // Primeiro login: completa os dados antes de entrar no painel.
  if (!sessao.data.usuario.perfilCompleto) {
    return <PaginaDePrimeiroAcesso usuario={sessao.data.usuario} />;
  }

  return (
    // `key`: trocar de conta recria a selecao de cliente do zero.
    <ProvedorDeClienteSelecionado key={sessao.data.usuario.id} usuarioId={sessao.data.usuario.id}>
      <AppLogado usuario={sessao.data.usuario} caminho={caminho} />
    </ProvedorDeClienteSelecionado>
  );
}

/** Le o token do link uma vez e o tira da barra de endereco. */
function PaginaDeSenha({
  finalidade,
  aoConcluir,
}: {
  readonly finalidade: "convite" | "redefinicao";
  readonly aoConcluir: (mensagem: string) => void;
}) {
  const [token] = useState(() => parametroDaUrl("token"));
  useEffect(() => {
    if (window.location.search) window.history.replaceState(null, "", window.location.pathname);
  }, []);

  return (
    <PaginaDefinirSenha
      token={token}
      finalidade={finalidade}
      aoConcluir={() =>
        aoConcluir(
          finalidade === "convite"
            ? "Senha criada. Agora é só entrar."
            : "Senha alterada. Entre com a senha nova.",
        )
      }
    />
  );
}

type UsuarioLogado = {
  readonly id: string;
  readonly nome: string;
  readonly email: string;
  readonly papel: "admin" | "usuario";
  readonly telefone: string | null;
  readonly cargo: string | null;
};

function AppLogado({ usuario, caminho }: { readonly usuario: UsuarioLogado; readonly caminho: string }) {
  const sair = useSair();
  const equipeMonte = podeGerenciarUsuarios(usuario.papel);
  const loginRecente = useLoginRecente();
  // Logo depois do login, a equipe Monte ve as contas com saldo baixo.
  useAlertaDeSaldoNoLogin(loginRecente && equipeMonte);
  const { selecionado } = useClienteSelecionado();

  const cargoNoPainel = equipeMonte
    ? ROTULO_DO_PAPEL.admin
    : selecionado?.meuPapel
      ? ROTULO_DO_PAPEL_NO_CLIENTE[selecionado.meuPapel]
      : ROTULO_DO_PAPEL[usuario.papel];
  const contexto: ContextoDoMenu = {
    nomeDoUsuario: usuario.nome,
    equipeMonte,
    papelNoCliente: selecionado?.meuPapel ?? null,
    cliente: selecionado?.nome ?? null,
    clienteId: selecionado?.id ?? null,
    conta: {
      nome: usuario.nome,
      email: usuario.email,
      telefone: usuario.telefone,
      cargo: usuario.cargo,
      cargoNoPainel,
    },
  };
  const pagina = paginaPorCaminho(caminho);
  const daCliente = pagina ? AREAS_DO_CLIENTE.has(pagina.area) : false;

  return (
    <SidebarProvider defaultOpen={!estaNaCentral(caminho)} key={estaNaCentral(caminho) ? "central" : "app"}>
      {selecionado ? <AvisoDeSincronizacao key={selecionado.id} clienteId={selecionado.id} /> : null}
      <AppSidebar
        cabecalho={<SeletorDeCliente podeAdicionar={equipeMonte} />}
        grupos={gruposDoMenu(contexto)}
        usuario={{
          nome: usuario.nome,
          email: usuario.email,
          iniciais: iniciaisDe(usuario.nome),
          // Cargo: Administrador (equipe Monte), ou Gestor/Membro na clinica selecionada.
          descricao: cargoNoPainel,
        }}
        saindo={sair.isPending}
        aoAbrirPerfil={() => navegar("/perfil")}
        aoSair={() => sair.mutate(undefined, { onSettled: () => navegar("/entrar", { substituir: true }) })}
      />
      {/* min-w-0: tabela larga (ex.: Funil Projetado) rola dentro dela, sem empurrar a pagina. */}
      <SidebarInset className="min-w-0">
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4 md:px-6">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 data-[orientation=vertical]:h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                {pagina ? (
                  <>
                    <BreadcrumbItem className="hidden md:block">
                      {daCliente && selecionado ? selecionado.nome : tituloDaArea(pagina)}
                    </BreadcrumbItem>
                    <BreadcrumbSeparator className="hidden md:block" />
                  </>
                ) : null}
                <BreadcrumbItem>
                  <BreadcrumbPage>{pagina?.titulo ?? "Página não encontrada"}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </header>
        <main className="flex flex-1 flex-col px-6 pt-4 pb-10 md:px-pagina md:pt-6 md:pb-pagina">
          {!pagina ? (
            <EstadoVazio titulo="Página não encontrada" descricao="Use o menu ao lado para navegar." />
          ) : !podeVer(pagina, contexto) ? (
            <EstadoDeErro titulo="Sem permissão" mensagem="Esta área não está disponível para o seu acesso." />
          ) : daCliente && !selecionado ? (
            <EstadoVazio
              titulo={`Olá, ${usuario.nome.split(" ")[0] ?? usuario.nome}.`}
              descricao={
                equipeMonte
                  ? "Cadastre o primeiro cliente pelo seletor no topo da barra lateral."
                  : "Você ainda não tem acesso a nenhuma clínica. Fale com a equipe Monte Saúde."
              }
              icone={<Building2 className="size-6" aria-hidden="true" />}
            />
          ) : (
            pagina.renderizar(contexto)
          )}
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
